import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import { motion } from "framer-motion";
import { fadeUp, useMotion } from "../../ui/motion";
import { FiSearch, FiShoppingCart, FiEye, FiShoppingBag } from "react-icons/fi";
import { mobile } from "../../ui/media";
import SafeImage from "../../ui/SafeImage";
import FarmerJoinBand from "./FarmerJoinBand";
import NewsletterSignup from "../Newsletter/NewsletterSignup";
import { useCart } from "../../context/CartContext";
import { useToast } from "../../context/ToastContext";
import { getProducts as fetchProducts, getBaskets as fetchBaskets } from "../../Utils/storeService";
import { formatLek } from "../../Utils/currency";
import { isLowAvailability, isSoldOut, readAvailability } from "../../Utils/availability";

/* ─── Shared glass mixin ─── */
const glass = css`
  background: ${({ theme }) => theme.colors.background.glass};
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid ${({ theme }) => theme.colors.background.glassBorder};
`;

/* ─── Page ─── */
const Page = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background.app};
`;

/* ─── Banner ─── */
const Banner = styled.section`
  background: ${({ theme }) => theme.colors.background.surfaceAlt};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(2)}`};
  text-align: center;
`;

const BannerInner = styled.div`
  max-width: 560px;
  margin: 0 auto;
`;

const Tagline = styled.h1`
  margin: 0 0 4px;
  font-size: 1.4rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};

  ${mobile} {
    font-size: 1.15rem;
  }
`;

const Subtitle = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(1.5)};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SearchWrap = styled.div`
  position: relative;
  max-width: 420px;
  margin: 0 auto;
`;

const SearchIconEl = styled(FiSearch)`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.input.placeholder};
  font-size: 16px;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  border: none;
  border-radius: 100px;
  ${glass}
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.9rem;
  padding: 10px 16px 10px 40px;
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
  transition: box-shadow 0.2s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.input.placeholder};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.colors.shadow.medium};
  }
`;

/* ─── Basket finder CTA ─── */
const FinderCTA = styled.section`
  max-width: 1200px;
  margin: ${({ theme }) => theme.spacing(2)} auto 0;
  padding: ${({ theme }) => `0 ${theme.spacing(2)}`};
`;

const FinderCard = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  text-align: left;
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.accent.primary};
  border-radius: ${({ theme }) => `${theme.radius.lg}px`};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
  transition: box-shadow ${({ theme }) => theme.motion.fast} ease,
    transform ${({ theme }) => theme.motion.fast} ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      box-shadow: ${({ theme }) => theme.colors.shadow.medium};
      transform: translateY(-2px);
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.375)}
      ${({ theme }) => theme.colors.border.focus};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none;
  }
`;

const FinderIcon = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 2rem;
  line-height: 1;
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.accent.primary};
`;

const FinderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.25)};
  flex: 1;
`;

const FinderTitle = styled.span`
  font-weight: 700;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FinderSub = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FinderGo = styled.span`
  flex: 0 0 auto;
  font-weight: 700;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.accent.primary};
`;

/* ─── Sections ─── */
const Section = styled.section`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(2)}`};

  ${mobile} {
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(1.5)}`};
  }
`;

const SectionInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ViewAllLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.accent.primary};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const SortSelect = styled.select`
  border: 1px solid ${({ theme }) => theme.colors.input.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.input.bg};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 6px 10px;
  font-size: 0.75rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
  }
`;

/* ─── Category filter rail ─── */
const FilterRail = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  overflow-x: auto;
  padding: ${({ theme }) => `${theme.spacing(0.5)} 0 ${theme.spacing(1.5)}`};
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const FilterChip = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  min-height: ${({ theme }) => theme.spacing(5.5)};
  padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.accent.primary : theme.colors.border.subtle};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.accent.primary : theme.colors.background.surface};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.text.inverse : theme.colors.text.primary};
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    color ${({ theme }) => theme.motion.fast} ease,
    border-color ${({ theme }) => theme.motion.fast} ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: ${({ theme }) => theme.colors.accent.primary};
      color: ${({ theme, $active }) =>
        $active ? theme.colors.text.inverse : theme.colors.accent.primary};
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
      ${({ theme }) => theme.colors.border.focus};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${mobile} {
    min-height: ${({ theme }) => theme.spacing(6)};
  }
`;

/* ─── Basket horizontal scroll (hidden scrollbar) ─── */
const BasketScroll = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0 8px;

  /* Hide scrollbar on all platforms */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }
`;

const BasketCard = styled(motion.div)`
  flex: 0 0 220px;
  scroll-snap-align: start;
  ${glass}
  border-radius: 16px;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-3px);
      box-shadow: ${({ theme }) => theme.colors.shadow.medium};
    }
  }

  ${mobile} {
    flex: 0 0 190px;
  }
`;

const BasketImg = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 2;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const BasketBody = styled.div`
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const BasketName = styled.h3`
  margin: 0;
  font-size: 0.85rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const BasketDesc = styled.p`
  margin: 0;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  margin-top: 2px;
`;

const Chip = styled.span`
  padding: 1px 5px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.state.hover};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 9px;
  font-weight: 600;
`;

const BasketFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 6px;
`;

const BasketPrice = styled.span`
  font-weight: 800;
  color: ${({ theme }) => theme.colors.accent.primary};
  font-size: 1rem;
`;

const SmallBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;
  white-space: nowrap;

  &:active {
    transform: scale(0.96);
  }
`;

const AddBtn = styled(SmallBtn)`
  background: ${({ theme }) => theme.colors.accent.primary};
  color: ${({ theme }) => theme.colors.text.inverse};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    background: ${({ theme }) => theme.colors.background.surfaceAlt};
    color: ${({ theme }) => theme.colors.text.disabled};
  }
`;

const AvailabilityChip = styled.span`
  align-self: flex-start;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid
    ${({ theme, $variant }) =>
      $variant === "danger"
        ? theme.colors.accent.danger
        : theme.colors.accent.warning};
  color: ${({ theme, $variant }) =>
    $variant === "danger"
      ? theme.colors.accent.danger
      : theme.colors.text.primary};
  font-size: 9px;
  font-weight: 700;
`;

const ViewBtn = styled(SmallBtn)`
  background: transparent;
  color: ${({ theme }) => theme.colors.accent.primary};
  border: 1px solid ${({ theme }) => theme.colors.accent.primary};

  &:hover {
    background: ${({ theme }) => theme.colors.accent.primary}10;
  }
`;

/* ─── Products grid ─── */
const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;

  ${mobile} {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
`;

const ProductCard = styled(motion.div)`
  ${glass}
  border-radius: 16px;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-3px);
      box-shadow: ${({ theme }) => theme.colors.shadow.medium};
    }
  }
`;

const ProductBody = styled.div`
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
`;

const ProductName = styled.h3`
  margin: 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FarmLabel = styled.span`
  font-size: 0.68rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const TagRow = styled.div`
  display: flex;
  gap: 3px;
  flex-wrap: wrap;
  margin-top: 2px;
`;

const Tag = styled.span`
  padding: 1px 5px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.accent.primary}15;
  color: ${({ theme }) => theme.colors.accent.primary};
  font-size: 9px;
  font-weight: 600;
`;

const StockBadge = styled.span`
  padding: 1px 5px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.accent.danger}15;
  color: ${({ theme }) => theme.colors.accent.danger};
  font-size: 9px;
  font-weight: 600;
`;

const ProductFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 6px;
`;

const ProductPrice = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.accent.primary};
  font-size: 0.9rem;
`;

const PriceUnit = styled.span`
  font-weight: 400;
  font-size: 0.68rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/* ─── Loading ─── */
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Skeleton = styled.div`
  border-radius: 16px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.background.surfaceAlt} 25%,
    ${({ theme }) => theme.colors.background.surface} 50%,
    ${({ theme }) => theme.colors.background.surfaceAlt} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  height: ${({ $h }) => $h || "200px"};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing(5)} 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.9rem;
`;

const CTARow = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const CTAButton = styled.button`
  background: ${({ theme }) => theme.colors.accent.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: 12px;
  padding: 12px 28px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.15s ease;

  &:hover {
    opacity: 0.9;
  }
  &:active {
    transform: scale(0.97);
  }
`;

/* ─── Animation ─── */


/* ─── Component ─── */
const HomepageMarketplace = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reduce } = useMotion();
  const { addItemToCart } = useCart();
  const { success } = useToast();

  const [baskets, setBaskets] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingBaskets, setLoadingBaskets] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("default");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeBadge, setActiveBadge] = useState("all");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let active = true;
    fetchBaskets()
      .then((d) => {
        if (!active) return;
        setBaskets(Array.isArray(d) ? d : []);
      })
      .catch(() => { if (active) setBaskets([]); })
      .finally(() => { if (active) setLoadingBaskets(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    fetchProducts()
      .then((d) => {
        if (!active) return;
        if (!Array.isArray(d)) { setProducts([]); return; }
        setProducts(d.filter((p) => p.stock > 0).map((p) => ({
          id: p.id, name: p.name, farm: p.farmName || "",
          image: p.imageUrl,
          badges: p.badges || [], price: p.price, unit: p.unit, stock: p.stock,
        })));
      })
      .catch(() => { if (active) setProducts([]); })
      .finally(() => { if (active) setLoadingProducts(false); });
    return () => { active = false; };
  }, []);

  // Build the filter rail from the badges products actually carry, so the rail
  // reflects live inventory rather than a hardcoded taxonomy.
  const availableBadges = useMemo(() => {
    const set = new Set();
    products.forEach((p) => (p.badges || []).forEach((b) => b && set.add(b)));
    return Array.from(set);
  }, [products]);

  const filteredProducts = useMemo(() => {
    let list = products;
    if (activeBadge !== "all") {
      list = list.filter((p) => (p.badges || []).includes(activeBadge));
    }
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || (p.farm && p.farm.toLowerCase().includes(q)));
    }
    switch (sort) {
      case "price-asc":  list = [...list].sort((a, b) => a.price - b.price); break;
      case "price-desc": list = [...list].sort((a, b) => b.price - a.price); break;
      case "name":       list = [...list].sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return list;
  }, [products, debouncedSearch, sort, activeBadge]);

  const handleAddBasket = useCallback((basket) => {
    const added = addItemToCart({
      id: basket.id,
      farmId: basket.farmId,
      farmName: basket.farmName || basket.farm?.name || "",
      name: basket.name,
      price: Number(basket.price),
      image: basket.imageUrl || "",
      availableQuantity: basket.availableQuantity,
    });
    if (added) {
      success(t("baskets_featured.added"));
    }
  }, [addItemToCart, success, t]);

  return (
    <Page>
      {/* Banner */}
      <Banner>
        <BannerInner>
          <Tagline>{t("hero.title")}</Tagline>
          <Subtitle>{t("hero.subtext")}</Subtitle>
          <SearchWrap>
            <SearchIconEl />
            <SearchInput
              type="text"
              placeholder={t("marketplace.search_placeholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchWrap>
        </BannerInner>
      </Banner>

      {/* Basket finder CTA */}
      <FinderCTA>
        <FinderCard
          type="button"
          onClick={() => navigate("/find-my-basket")}
          aria-label={t("find_basket.cta_aria", {
            defaultValue: "Find your perfect basket",
          })}
        >
          <FinderIcon aria-hidden="true"><FiShoppingBag /></FinderIcon>
          <FinderText>
            <FinderTitle>
              {t("find_basket.cta_title", {
                defaultValue: "Not sure what to pick?",
              })}
            </FinderTitle>
            <FinderSub>
              {t("find_basket.cta_text", {
                defaultValue:
                  "Answer 4 quick questions and we'll match you with the perfect basket.",
              })}
            </FinderSub>
          </FinderText>
          <FinderGo aria-hidden="true">→</FinderGo>
        </FinderCard>
      </FinderCTA>

      <FarmerJoinBand />

      {/* Baskets */}
      <Section>
        <SectionInner>
          <SectionHeader>
            <SectionTitle>{t("marketplace.baskets_title")}</SectionTitle>
            <ViewAllLink onClick={() => navigate("/baskets")}>
              {t("baskets_featured.view_all")} →
            </ViewAllLink>
          </SectionHeader>
          {loadingBaskets ? (
            <BasketScroll>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} $h="200px" style={{ flex: "0 0 220px", borderRadius: "16px" }} />
              ))}
            </BasketScroll>
          ) : (
            <BasketScroll>
              {baskets.map((b) => {
                const available = readAvailability(b);
                const soldOut = isSoldOut(available);
                return (
                <BasketCard key={b.id} variants={fadeUp(reduce)} initial="hidden" whileInView="show" viewport={{ once: true }} style={{ cursor: "pointer" }} onClick={() => navigate(`/baskets/${b.id}`)}>
                  <BasketImg>
                    <SafeImage src={b.imageUrl} alt={b.name} aspectRatio="3 / 2" />
                  </BasketImg>
                  <BasketBody>
                    <BasketName>{b.name}</BasketName>
                    {b.description && <BasketDesc>{b.description}</BasketDesc>}
                    {b.items && b.items.length > 0 && (
                      <ChipRow>
                        {b.items.slice(0, 3).map((item) => (
                          <Chip key={item.productName || item.productId}>
                            {item.productName} ×{item.quantity}
                          </Chip>
                        ))}
                        {b.items.length > 3 && <Chip>+{b.items.length - 3}</Chip>}
                      </ChipRow>
                    )}
                    {soldOut ? (
                      <AvailabilityChip $variant="danger">
                        {t("baskets.sold_out", { defaultValue: "Sold out" })}
                      </AvailabilityChip>
                    ) : isLowAvailability(available) ? (
                      <AvailabilityChip $variant="warning">
                        {t("baskets.only_left", { count: available, defaultValue: "Only {{count}} left" })}
                      </AvailabilityChip>
                    ) : null}
                    <BasketFooter>
                      <BasketPrice>{formatLek(b.price)}</BasketPrice>
                      <AddBtn
                        disabled={soldOut}
                        onClick={(e) => { e.stopPropagation(); handleAddBasket(b); }}
                      >
                        <FiShoppingCart size={12} />
                        {soldOut
                          ? t("baskets.sold_out", { defaultValue: "Sold out" })
                          : t("marketplace.add_to_cart")}
                      </AddBtn>
                    </BasketFooter>
                  </BasketBody>
                </BasketCard>
                );
              })}
            </BasketScroll>
          )}
        </SectionInner>
      </Section>

      {/* Products */}
      <Section style={{ paddingTop: 0 }}>
        <SectionInner>
          <SectionHeader>
            <SectionTitle>{t("marketplace.products_title")}</SectionTitle>
            <SortSelect value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="default">{t("marketplace.sort_default")}</option>
              <option value="price-asc">{t("marketplace.sort_price_asc")}</option>
              <option value="price-desc">{t("marketplace.sort_price_desc")}</option>
              <option value="name">{t("marketplace.sort_name")}</option>
            </SortSelect>
          </SectionHeader>

          {!loadingProducts && availableBadges.length > 0 && (
            <FilterRail
              role="group"
              aria-label={t("marketplace.filter_label", { defaultValue: "Filter by category" })}
            >
              <FilterChip
                type="button"
                $active={activeBadge === "all"}
                aria-pressed={activeBadge === "all"}
                onClick={() => setActiveBadge("all")}
              >
                {t("marketplace.filter_all", { defaultValue: "All" })}
              </FilterChip>
              {availableBadges.map((badge) => (
                <FilterChip
                  key={badge}
                  type="button"
                  $active={activeBadge === badge}
                  aria-pressed={activeBadge === badge}
                  onClick={() => setActiveBadge(badge)}
                >
                  {badge}
                </FilterChip>
              ))}
            </FilterRail>
          )}

          {loadingProducts ? (
            <ProductsGrid>
              {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} $h="230px" />)}
            </ProductsGrid>
          ) : filteredProducts.length === 0 ? (
            <EmptyState>
              {t("marketplace.no_results")}
              {(activeBadge !== "all" || debouncedSearch) && (
                <div style={{ marginTop: 12 }}>
                  <ViewAllLink
                    onClick={() => {
                      setActiveBadge("all");
                      setSearch("");
                    }}
                  >
                    {t("marketplace.clear_filters", { defaultValue: "Clear filters" })}
                  </ViewAllLink>
                </div>
              )}
            </EmptyState>
          ) : (
            <ProductsGrid>
              {filteredProducts.map((p) => (
                <ProductCard key={p.id} variants={fadeUp(reduce)} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-20px" }}>
                  <SafeImage src={p.image} alt={p.name} aspectRatio="4/3" />
                  <ProductBody>
                    <ProductName>{p.name}</ProductName>
                    {p.farm ? <FarmLabel>{p.farm}</FarmLabel> : null}
                    <TagRow>
                      {p.badges && p.badges.map((b) => <Tag key={b}>{b}</Tag>)}
                      {p.stock > 0 && p.stock <= 5 ? <StockBadge>{t("featured.low_stock")}</StockBadge> : null}
                    </TagRow>
                    <ProductFooter>
                      <ProductPrice>
                        {formatLek(p.price)}
                        {p.unit ? <PriceUnit>/{p.unit}</PriceUnit> : null}
                      </ProductPrice>
                      <ViewBtn onClick={() => navigate(`/products/${p.id}`)}>
                        <FiEye size={12} />
                        {t("marketplace.view_product")}
                      </ViewBtn>
                    </ProductFooter>
                  </ProductBody>
                </ProductCard>
              ))}
            </ProductsGrid>
          )}

          <CTARow>
            <CTAButton onClick={() => navigate("/farms")}>
              {t("marketplace.browse_farms")}
            </CTAButton>
          </CTARow>
        </SectionInner>
      </Section>

      <NewsletterSignup source="home" />
    </Page>
  );
};

export default HomepageMarketplace;
